import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Message,
  Modal,
  Select,
  Icon,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import { LocationState } from '../../utilities/commonData';
import '../../assets/semantic-ui-fix.css';
import { DealerInfoAction } from '../DealerManager/ListRow';

const dealerInformationInit = {
  customerNumber: '',
  customerName: '',
  address1: '',
  address2: '',
  customerCity: '',
  customerState: '',
  customerZip: '',
  customerPhone: '',
  customerEmail: '',
  customerType: '',
  isRep: false,
};

const DealerType = [
  'National',
  'Dealer Fleet',
  'Major Dealer',
  'Franchise',
  'Regional Account',
  'Independent',
  'EDP',
];

function DealerInformationForm(props) {
  const {
    isModalOpen,
    title,
    onCloseHandler,
    isLoading,
    onSubmitHandler,
    errorMessage,
    onClearError,
    initialData,
    isEdit,
  } = props;

  const [errors, setErrors] = useState({});
  const [dealerTypeOptions, setDealerTypeOptions] = useState([]);
  const [formData, setFormData] = useState(dealerInformationInit);

  const onClose = useCallback(() => {
    onCloseHandler();
    setFormData(dealerInformationInit);
    setErrors({});
  }, [onCloseHandler]);

  // Reset form when modal opens
  useEffect(() => {
    if (isModalOpen) {
      if (isEdit && initialData) {
        // Populate form with existing data for editing
        setFormData({
          customerNumber: initialData.customerNumber || '',
          customerName: initialData.customerName || '',
          address1: initialData.address1 || '',
          address2: initialData.address2 || '',
          customerCity: initialData.customerCity || '',
          customerState: initialData.customerState || '',
          customerZip: initialData.customerZip || '',
          customerPhone: initialData.customerPhone || '',
          customerEmail: initialData.customerEmail || '',
          customerType: initialData.customerType || '',
          isRep: initialData.isRep || false,
          id: initialData.id,
        });
      } else {
        // Reset to initial state for creating new
        setFormData(dealerInformationInit);
      }
      setErrors({});
    }
  }, [isModalOpen, isEdit, initialData]);

  useEffect(() => {
    const selectOption = {
      key: 'select',
      text: 'Select dealer type',
      value: '',
      style: { fontWeight: 'normal', opacity: 0.6 },
    };

    const typeOptions = DealerType.map(type => ({
      key: type,
      text: type,
      value: type,
    }));

    setDealerTypeOptions([selectOption, ...typeOptions]);
  }, []);

  const validate = useCallback(() => {
    const errs = {};

    const dealerNumber = formData.customerNumber.trim();
    if (!dealerNumber) {
      errs.customerNumber = ['Dealer# is required.'];
    } else if (
      !/^\d{1,9}$/.test(dealerNumber)
        || (
          (() => {
            const num = parseInt(dealerNumber, 10);
            return !(
              (num >= 4000000 && num <= 5999999)
                  || (num >= 100000000 && num <= 199999999)
            );
          })()
        )
    ) {
      errs.customerNumber = [
        `Dealer ${dealerNumber} must be numeric, up to 9 characters, and in range 4000000–5999999 or 100000000–199999999.`,
      ];
    }

    if (!formData.customerName.trim()) {
      errs.customerName = ['Dealer name is required.'];
    } else if (formData.customerName.trim().length > 50) {
      errs.customerName = ['Dealer name must be up to 50 characters.'];
    }

    if (!formData.address1.trim()) {
      errs.address1 = ['Street address is required.'];
    } else if (formData.address1.trim().length > 100) {
      errs.address1 = ['Address 1 must be up to 100 characters.'];
    }
    if (formData.address2.trim().length > 100) {
      errs.address2 = ['Address 2 must be up to 100 characters.'];
    }

    if (!formData.customerState.trim()) {
      errs.customerState = ['State is required.'];
    }

    if (!formData.customerCity.trim()) {
      errs.customerCity = ['City is required.'];
    } else if (formData.customerCity.trim().length > 25) {
      errs.customerCity = ['City must be up to 25 characters.'];
    }

    if (!formData.customerZip.trim()) {
      errs.customerZip = ['Zipcode is required.'];
    } else if (
      !/^[A-Za-z0-9]{5}(-[A-Za-z0-9]{4})?$/.test(formData.customerZip.trim())
    ) {
      errs.customerZip = ['Zipcode must be in the format ##### or #####-####.'];
    }

    if (!formData.customerPhone.trim()) {
      errs.customerPhone = ['Phone number is required.'];
    } else if (!/^\d+$/.test(formData.customerPhone.trim()) || formData.customerPhone.trim().length !== 10) {
      errs.customerPhone = ['Phone number must be numeric and only 10 characters.'];
    }

    if (!formData.customerEmail.trim()) {
      errs.customerEmail = ['Email is required.'];
    } else if (formData.customerEmail.trim().length > 100) {
      errs.customerEmail = ['Email just allow maximum 100 characters.'];
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail.trim()) || formData.customerEmail.trim().length > 100
    ) {
      errs.customerEmail = ['Email must be in the format username@example.com and up to 100 characters.'];
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleChange = (_, {
    name, value, checked, type,
  }) => {
    let newValue = value;

    // Handle "Select Dealer type" option selection
    if (name === 'customerType' && newValue === '') {
      setFormData(prev => ({
        ...prev,
        customerType: '',
      }));
      if (errors.customerType && errors.customerType.length > 0) {
        setErrors(prev => ({ ...prev, customerType: [] }));
      }
      return;
    }

    if (name === 'customerNumber') {
      // Remove any non-numeric characters
      newValue = newValue.replace(/\D/g, '');
    }

    if (name === 'customerPhone') {
      // Remove any non-numeric characters
      newValue = newValue.replace(/\D/g, '');
    }

    if (name === 'customerZip') {
      // Ensure only one dash and in correct position
      const parts = newValue.split('-');
      if (parts.length > 2) {
        newValue = parts[0] + '-' + parts.slice(1).join('');
      }

      if (newValue.length > 10) {
        newValue = newValue.substring(0, 10);
      }

      if (newValue.length === 6 && !newValue.includes('-')) {
        newValue = newValue.substring(0, 5) + '-' + newValue.substring(5);
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : newValue,
    }));

    if (errors[name] && errors[name].length > 0) {
      setErrors(prev => ({ ...prev, [name]: [] }));
    }
  };

  const submitForm = useCallback(
    async e => {
      e.preventDefault();
      if (isLoading) return;

      if (validate()) {
        onSubmitHandler({
          ...formData,
          action: isEdit ? DealerInfoAction.HANDLE_EDIT : DealerInfoAction.HANDLE_CREATE,
        });
      }
    },
    [formData, isLoading, onSubmitHandler, validate, isEdit]
  );

  return (
    <Modal
      size='large'
      closeIcon
      onClose={onClose}
      open={isModalOpen}
      closeOnEscape
      closeOnRootNodeClick={false}
      className='dealer-information-form'
    >
      <Modal.Header style={{ borderBottom: 'none' }}>{title}</Modal.Header>
      <Modal.Content>
        {errorMessage && (
          <Message negative content={errorMessage} onDismiss={onClearError} />
        )}
        <Form>
          <Modal.Description>
            <Form.Group widths='equal'>
              <Form.Field
                inline
                required
                error={!isEmpty(errors.customerNumber)}
              >
                <label className='form-label' htmlFor='customerNumber'>Dealer#</label>
                <Input
                  id='customerNumber'
                  name='customerNumber'
                  autoComplete='off'
                  value={formData.customerNumber}
                  onChange={handleChange}
                  pattern='[0-9]*'
                  inputMode='numeric'
                  readOnly={isEdit}
                  disabled={isEdit}
                />
                {errors.customerNumber && (
                  <span className='error-msg'>
                    {errors.customerNumber.join(' ')}
                  </span>
                )}
              </Form.Field>
            </Form.Group>

            <Form.Field inline required error={!isEmpty(errors.customerName)}>
              <label className='form-label' htmlFor='customerName'>Name</label>
              <Input
                id='customerName'
                name='customerName'
                autoComplete='off'
                value={formData.customerName}
                onChange={handleChange}
              />
              {errors.customerName && (
                <span className='error-msg'>
                  {errors.customerName.join(' ')}
                </span>
              )}
            </Form.Field>
            <Form.Field inline>
              <label className='form-label' htmlFor='isRep'>Is Rep</label>
              <Checkbox
                id='isRep'
                name='isRep'
                checked={formData.isRep}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field inline required error={!isEmpty(errors.address1)}>
              <label className='form-label' htmlFor='address1'>Address 1</label>
              <Input
                id='address1'
                name='address1'
                autoComplete='off'
                value={formData.address1}
                onChange={handleChange}
              />
              {errors.address1 && (
                <span className='error-msg'>{errors.address1.join(' ')}</span>
              )}
            </Form.Field>
            <Form.Field inline>
              <label className='form-label' htmlFor='address2'>Address 2</label>
              <Input
                id='address2'
                name='address2'
                autoComplete='off'
                value={formData.address2}
                onChange={handleChange}
              />
              {errors.address2 && (
              <span className='error-msg'>{errors.address2.join(' ')}</span>
              )}
            </Form.Field>
            <Form.Field inline required error={!isEmpty(errors.customerCity)}>
              <label className='form-label' htmlFor='customerCity'>City</label>
              <Input
                id='customerCity'
                name='customerCity'
                autoComplete='off'
                value={formData.customerCity}
                onChange={handleChange}
              />
              {errors.customerCity && (
                <span className='error-msg'>
                  {errors.customerCity.join(' ')}
                </span>
              )}
            </Form.Field>
            <Form.Field inline required error={!isEmpty(errors.customerState)}>
              <label className='form-label' htmlFor='customerState'>State</label>
              <Select
                id='customerState'
                name='customerState'
                options={LocationState}
                value={formData.customerState}
                onChange={handleChange}
                placeholder='Select state'
              />
              {errors.customerState && (
                <span className='error-msg'>
                  {errors.customerState.join(' ')}
                </span>
              )}
            </Form.Field>
            <Form.Field inline required error={!isEmpty(errors.customerZip)}>
              <label className='form-label' htmlFor='customerZip'>Zipcode</label>
              <Input
                id='customerZip'
                name='customerZip'
                autoComplete='off'
                value={formData.customerZip}
                onChange={handleChange}
                placeholder='##### or #####-####'
                maxLength='10'
              />
              {errors.customerZip && (
                <span className='error-msg'>
                  {errors.customerZip.join(' ')}
                </span>
              )}
            </Form.Field>
            <Form.Field inline required error={!isEmpty(errors.customerPhone)}>
              <label className='form-label' htmlFor='customerPhone'>Phone#</label>
              <Input
                id='customerPhone'
                name='customerPhone'
                autoComplete='off'
                value={formData.customerPhone}
                onChange={handleChange}
                pattern='[0-9]*'
                inputMode='numeric'
                placeholder='XXXXXXXXXX'
              />
              {errors.customerPhone && (
                <span className='error-msg'>
                  {errors.customerPhone.join(' ')}
                </span>
              )}
            </Form.Field>
            <Form.Field inline required error={!isEmpty(errors.customerEmail)}>
              <label className='form-label' htmlFor='customerEmail'>Email</label>
              <Input
                id='customerEmail'
                name='customerEmail'
                autoComplete='off'
                value={formData.customerEmail}
                onChange={handleChange}
              />
              {errors.customerEmail && (
                <span className='error-msg'>
                  {errors.customerEmail.join(' ')}
                </span>
              )}
            </Form.Field>
            <Form.Field inline>
              <label className='form-label' htmlFor='customerType'>Dealer Type</label>
              <Select
                id='customerType'
                name='customerType'
                options={dealerTypeOptions}
                value={formData.customerType}
                onChange={handleChange}
                placeholder='Select dealer type'
              />
            </Form.Field>
          </Modal.Description>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        {isLoading && (
          <Button loading positive>
            Submitting...
          </Button>
        )}
        {!isLoading && (
          <Button color='green' onClick={submitForm} inverted>
            <Icon name='checkmark' /> Submit
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
      </Modal.Actions>
    </Modal>
  );
}

DealerInformationForm.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onSubmitHandler: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  onClearError: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    customerNumber: PropTypes.string,
    customerName: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    customerCity: PropTypes.string,
    customerState: PropTypes.string,
    customerZip: PropTypes.string,
    customerPhone: PropTypes.string,
    customerEmail: PropTypes.string,
    customerType: PropTypes.string,
    isRep: PropTypes.bool,
  }),
  isEdit: PropTypes.bool,
};

DealerInformationForm.defaultProps = {
  isLoading: false,
  errorMessage: '',
  initialData: null,
  isEdit: false,
};

export default DealerInformationForm;
